import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { supabase } from "@/lib/supabase";
import { invalidateFor } from "@/lib/cache-registry";
import { normalizeSupplierName } from "@/lib/freelancer-projects";
import type { DBFreelancerSupplier, FreelanceSupplierOverviewRow } from "@/lib/db-types";

/**
 * VIB-2927: PowerOffice-leverandør koblet til frilanser. Koblingen gjelder alle
 * bilag fra leverandøren, bakover og framover. Audit i event_log via trigger.
 */

export function useFreelancerSupplierLinks(enabled = true) {
  return useQuery({
    queryKey: ["freelancer-suppliers"],
    enabled,
    queryFn: async (): Promise<DBFreelancerSupplier[]> => {
      const { data, error } = await supabase.from("freelancer_suppliers").select("*");
      if (error) throw error;
      return (data ?? []) as DBFreelancerSupplier[];
    },
  });
}

/** Alle leverandører med leverandørfakturaer på prosjekter, med koblingsstatus og forslag. */
export function useFreelanceSupplierOverview(enabled = true) {
  return useQuery({
    queryKey: ["freelance-supplier-overview"],
    enabled,
    staleTime: 5 * 60 * 1000,
    queryFn: async (): Promise<FreelanceSupplierOverviewRow[]> => {
      const { data, error } = await supabase.rpc("freelance_supplier_overview");
      if (error) throw error;
      return ((data ?? []) as FreelanceSupplierOverviewRow[]).map((r) => ({
        ...r,
        invoiced: Number(r.invoiced ?? 0),
        line_descriptions: r.line_descriptions ?? [],
      }));
    },
  });
}

/**
 * Kan innlogget bruker koble leverandører? Speiler RLS på freelancer_suppliers:
 * admin, eller PL/rådgiver på minst ett prosjekt. Spør databasens egne hjelpere
 * på den ekte sesjonen, som også er den skrivingen kjører på.
 */
export function useCanLinkFreelanceSuppliers(enabled = true): boolean {
  const { data } = useQuery({
    queryKey: ["can-link-freelance-suppliers"],
    enabled,
    staleTime: 5 * 60 * 1000,
    queryFn: async (): Promise<boolean> => {
      const [admin, lead] = await Promise.all([
        supabase.rpc("is_admin"),
        supabase.rpc("is_any_project_lead_or_advisor"),
      ]);
      if (admin.error) throw admin.error;
      if (lead.error) throw lead.error;
      return admin.data === true || lead.data === true;
    },
  });
  return data === true;
}

export interface SupplierLinkInput {
  /** Eksisterende kobling som endres, ellers ny. */
  linkId?: string | null;
  po_supplier_id: number | null;
  supplier_name: string;
  /** null = «Ikke frilanser». */
  freelancer_id: string | null;
}

export function useSetSupplierLink() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: SupplierLinkInput) => {
      const row = {
        po_supplier_id: input.po_supplier_id,
        supplier_key: normalizeSupplierName(input.supplier_name),
        supplier_name: input.supplier_name,
        freelancer_id: input.freelancer_id,
        is_not_freelancer: input.freelancer_id === null,
      };
      if (row.po_supplier_id == null && !row.supplier_key) throw new Error("Leverandøren mangler navn og PO-ID");
      const { error } = input.linkId
        ? await supabase.from("freelancer_suppliers").update(row).eq("id", input.linkId)
        : await supabase.from("freelancer_suppliers").insert(row);
      if (error) throw error;
    },
    onSuccess: async (_d, vars) => {
      await invalidateFor(qc, "freelancer_suppliers", { freelancerId: vars.freelancer_id });
      toast.success(vars.freelancer_id ? "Leverandøren er koblet til frilanseren" : "Merket som ikke frilanser");
    },
    onError: (err: Error) => toast.error(`Kunne ikke lagre koblingen: ${err.message}`),
  });
}

export function useRemoveSupplierLink() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ linkId }: { linkId: string; freelancerId: string | null }) => {
      const { error } = await supabase.from("freelancer_suppliers").delete().eq("id", linkId);
      if (error) throw error;
    },
    onSuccess: async (_d, vars) => {
      await invalidateFor(qc, "freelancer_suppliers", { freelancerId: vars.freelancerId });
      toast.success("Koblingen er fjernet");
    },
    onError: (err: Error) => toast.error(`Kunne ikke fjerne koblingen: ${err.message}`),
  });
}
